import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Save, Building2, Mail, FileText, PenTool, Upload, Trash2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { getSettings, updateSettings } from "@/services/localDatabase";

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firmaPreview, setFirmaPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    // Intestazione PDF
    intestazione_nome: "",
    intestazione_sede: "",
    intestazione_cap: "",
    intestazione_citta: "",
    intestazione_provincia: "",
    intestazione_ric: "",
    intestazione_concessione: "",
    // Produttore
    nome: "",
    sede_legale: "",
    cap: "",
    citta: "",
    provincia: "",
    ric: "",
    email: "",
    serie_ddr: "AP",
    // Firma
    firma_immagine: ""
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const settings = await getSettings();
      if (settings) {
        setFormData({
          // Intestazione
          intestazione_nome: settings.intestazione_nome || "",
          intestazione_sede: settings.intestazione_sede || "",
          intestazione_cap: settings.intestazione_cap || "",
          intestazione_citta: settings.intestazione_citta || "",
          intestazione_provincia: settings.intestazione_provincia || "",
          intestazione_ric: settings.intestazione_ric || "",
          intestazione_concessione: settings.intestazione_concessione || "",
          // Produttore
          nome: settings.nome || "",
          sede_legale: settings.sede_legale || "",
          cap: settings.cap || "",
          citta: settings.citta || "",
          provincia: settings.provincia || "",
          ric: settings.ric || "",
          email: settings.email || "",
          serie_ddr: settings.serie_ddr || "AP",
          firma_immagine: settings.firma_immagine || ""
        });
        if (settings.firma_immagine) {
          setFirmaPreview(settings.firma_immagine);
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success("Impostazioni salvate");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFirmaUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Seleziona un'immagine (PNG, JPG)");
      return;
    }

    if (file.size > 500000) {
      toast.error("L'immagine deve essere inferiore a 500KB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setFirmaPreview(base64);
      setFormData(prev => ({ ...prev, firma_immagine: base64 }));
      toast.success("Firma caricata");
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveFirma = () => {
    setFirmaPreview(null);
    setFormData(prev => ({ ...prev, firma_immagine: "" }));
    toast.success("Firma rimossa");
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-12">
        <div className="text-center text-slate-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
          Impostazioni
        </h1>
        <p className="text-slate-500 mt-1">Configura i dati per i DDR</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          
          {/* INTESTAZIONE PDF Section */}
          <Card className="border-green-200 border-2">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow text-green-700">
                <FileText className="w-5 h-5" />
                Dati Intestazione PDF
              </CardTitle>
              <CardDescription>
                Questi dati appariranno nell'intestazione di tutti i DDR generati
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="intestazione_nome">Nome / Ragione Sociale *</Label>
                  <Input
                    id="intestazione_nome"
                    value={formData.intestazione_nome}
                    onChange={(e) => handleChange("intestazione_nome", e.target.value)}
                    placeholder="Es. AZIENDA AGRICOLA MARE SRL"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="intestazione_sede">Sede</Label>
                  <Input
                    id="intestazione_sede"
                    value={formData.intestazione_sede}
                    onChange={(e) => handleChange("intestazione_sede", e.target.value)}
                    placeholder="Via Roma 123"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 md:col-span-1">
                  <div>
                    <Label htmlFor="intestazione_cap">CAP</Label>
                    <Input
                      id="intestazione_cap"
                      value={formData.intestazione_cap}
                      onChange={(e) => handleChange("intestazione_cap", e.target.value)}
                      placeholder="00100"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="intestazione_citta">Città</Label>
                    <Input
                      id="intestazione_citta"
                      value={formData.intestazione_citta}
                      onChange={(e) => handleChange("intestazione_citta", e.target.value)}
                      placeholder="Roma"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="intestazione_provincia">Prov.</Label>
                    <Input
                      id="intestazione_provincia"
                      value={formData.intestazione_provincia}
                      onChange={(e) => handleChange("intestazione_provincia", e.target.value.toUpperCase())}
                      placeholder="RM"
                      maxLength={2}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="intestazione_ric">P.IVA</Label>
                  <Input
                    id="intestazione_ric"
                    value={formData.intestazione_ric}
                    onChange={(e) => handleChange("intestazione_ric", e.target.value.toUpperCase())}
                    placeholder="IT12345678901"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="intestazione_concessione">Concessione</Label>
                  <Input
                    id="intestazione_concessione"
                    value={formData.intestazione_concessione}
                    onChange={(e) => handleChange("intestazione_concessione", e.target.value.toUpperCase())}
                    placeholder="N. 123/2024"
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PRODUTTORE Section */}
          <Card className="border-orange-200 border-2">
            <CardHeader className="bg-orange-50">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow text-orange-700">
                <User className="w-5 h-5" />
                Dati Registrazione PRODUTTORE
              </CardTitle>
              <CardDescription>
                Dati usati nel corpo del DDR quando emetti come Produttore (Serie: {formData.serie_ddr})
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="nome">Nome / Ragione Sociale *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange("nome", e.target.value)}
                    placeholder="Es. AZIENDA AGRICOLA MARE SRL"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="sede_legale">Sede Legale *</Label>
                  <Input
                    id="sede_legale"
                    value={formData.sede_legale}
                    onChange={(e) => handleChange("sede_legale", e.target.value)}
                    placeholder="Via Roma 123"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 md:col-span-1">
                  <div>
                    <Label htmlFor="cap">CAP</Label>
                    <Input
                      id="cap"
                      value={formData.cap}
                      onChange={(e) => handleChange("cap", e.target.value)}
                      placeholder="00100"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="citta">Città</Label>
                    <Input
                      id="citta"
                      value={formData.citta}
                      onChange={(e) => handleChange("citta", e.target.value)}
                      placeholder="Roma"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="provincia">Prov.</Label>
                    <Input
                      id="provincia"
                      value={formData.provincia}
                      onChange={(e) => handleChange("provincia", e.target.value.toUpperCase())}
                      placeholder="RM"
                      maxLength={2}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ric">Codice RIC</Label>
                  <Input
                    id="ric"
                    value={formData.ric}
                    onChange={(e) => handleChange("ric", e.target.value.toUpperCase())}
                    placeholder="IT123ABC"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="info@azienda.it"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="serie_ddr">Serie DDR Produttore</Label>
                  <Input
                    id="serie_ddr"
                    value={formData.serie_ddr}
                    onChange={(e) => handleChange("serie_ddr", e.target.value.toUpperCase())}
                    placeholder="AP"
                    maxLength={5}
                    className="mt-1 font-bold"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Firma */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <PenTool className="w-5 h-5 text-orange-500" />
                Firma e Timbro
              </CardTitle>
              <CardDescription>
                Carica un'immagine della tua firma che apparirà automaticamente nei DDR
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-48 h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                    {firmaPreview ? (
                      <img 
                        src={firmaPreview} 
                        alt="Firma" 
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-slate-400 text-sm">Nessuna firma</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFirmaUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full md:w-auto"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {firmaPreview ? "Cambia Firma" : "Carica Firma"}
                  </Button>
                  {firmaPreview && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRemoveFirma}
                      className="w-full md:w-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Rimuovi Firma
                    </Button>
                  )}
                  <p className="text-xs text-slate-500">
                    Formati: PNG, JPG (max 500KB)<br/>
                    Consigliato: sfondo trasparente
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit Button */}
        <div className="mt-6 flex justify-end">
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 h-11 px-8"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvataggio..." : "Salva Impostazioni"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
